import React, { useEffect, useState } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import crmLeadsImg from "../../../assets/crm-leads.png";
import crmQuotationsImg from "../../../assets/crm-quotations.png";
import crmProductsImg from "../../../assets/crm-products.png";
import crmWhatsappImg from "../../../assets/crm-whatsapp.png";
import crmReportsImg from "../../../assets/crm-reports.png";
import {
  MessageSquare,
  TrendingUp,
  Users,
  Target,
  Zap,
  BarChart3,
  ArrowRight
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

const CRM = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState("leads");

  const isMobile = screenWidth < 768;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Lead Management & Capture",
      description: "Automatically log leads from Facebook Ads, web forms, and emails, and assign them instantly to your sales team.",
      icon: <Target size={22} />
    },
    {
      title: "Visual Sales Pipeline",
      description: "Manage deals on drag-and-drop Kanban pipelines and calculate accurate monthly revenue forecasts.",
      icon: <TrendingUp size={22} />
    },
    {
      title: "WhatsApp & Email Sync",
      description: "Send automated updates, templates, and messages directly from contact profiles and log conversation logs.",
      icon: <MessageSquare size={22} />
    },
    {
      title: "Workflow Task Automation",
      description: "Set triggers for automated client emails, scheduled phone call alerts, and update fields dynamically.",
      icon: <Zap size={22} />
    },
    {
      title: "Customer Contact Timeline",
      description: "Review a unified timeline of contact notes, calls, document histories, and meeting notes in one clean screen.",
      icon: <Users size={22} />
    },
    {
      title: "Advanced Sales Dashboards",
      description: "Monitor agent performance, lead conversion ratios, response times, and monthly targets in real-time.",
      icon: <BarChart3 size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow" style={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)", width: "600px", height: "600px", opacity: 0.15, background: "radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)" }}></div>

        <div className="prod-hero-left">
          <span className="prod-badge">SALES CLOUD</span>
          <h1 className="prod-main-title">
            GTE CRM. <br />
            <span style={{ color: "#55FF60" }}>Accelerate Revenue.</span>
          </h1>
          <p className="prod-desc">
            Align sales pipelines, track lead stages, coordinate follow-ups, and automate customer conversations
            with direct WhatsApp integration.
          </p>
          <div className="prod-cta-group">
            {/* <a href="#contact" className="prod-primary-btn" style={{ padding: "16px 36px", fontSize: "16px", borderRadius: "999px", display: "inline-flex", alignItems: "center", gap: "8px" }}>
              Request Demo <ArrowRight size={18} />
            </a> */}
            <a
              href="https://crm.gtelinq.com/login"
              className="prod-secondary-btn"
              style={{ padding: "16px 36px", fontSize: "16px", borderRadius: "999px", display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              Login to CRM <ArrowRight size={18} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame" style={{
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            padding: isMobile ? "8px" : "12px",
            background: "rgba(10, 10, 10, 0.4)",
            borderColor: "rgba(59, 130, 246, 0.2)",
            boxShadow: "0 25px 80px rgba(59, 130, 246, 0.12)",
            backdropFilter: "blur(20px)"
          }}>
            <div className="mockup-header" style={{ paddingBottom: "8px", marginBottom: "6px" }}>
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">crm.gtelinq.com/leads</span>
            </div>
            <div className="mockup-content" style={{ display: "block", overflow: "hidden", padding: 0 }}>
              <img
                src={crmLeadsImg}
                alt="GTE CRM Lead Directory Dashboard"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: "0 0 12px 12px",
                  imageRendering: "-webkit-optimize-contrast"
                }}
              />
            </div>
          </div>
        </div>

        {/* DETAILED SPECIFICATIONS ROW (Quick Info about CRM) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: "30px",
          width: "100%",
          maxWidth: "1000px",
          marginTop: "20px",
          zIndex: 10
        }}>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ Direct Automation</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              Instantly logs leads from ads, social media channels, and web forms directly into active agent pipelines.
            </p>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ WhatsApp Native</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              Communicate with prospects directly in-app, syncing template replies and chat logs straight to contact timelines.
            </p>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ Invoicing & Ledger</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              One-click professional PDF quotation generation and dispatch system integrated with account ledger records.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">CRM CAPABILITIES</span>
          <h2 className="prod-section-title">Everything You Need to Scale Sales</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>


      {/* INTERACTIVE SHOWCASE SECTIONS */}
      <section className="prod-features-section" style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "80px", background: "#000" }}>
        <div className="prod-section-header" style={{ marginBottom: "50px" }}>
          <span className="prod-section-mini">CRM INTERACTIVE SHOWCASE</span>
          <h2 className="prod-section-title">Visual Tour of GTE CRM</h2>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", marginTop: "12px" }}>
            Explore how GTE CRM manages leads, chats, products, quotations, and analytics.
          </p>
        </div>

        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "40px",
          maxWidth: "1200px",
          margin: "0 auto",
          background: "rgba(255,255,255,0.01)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: "24px",
          padding: isMobile ? "20px" : "40px",
          boxShadow: "0 30px 60px rgba(0,0,0,0.8)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
          position: "relative"
        }}>
          {/* Left Navigation Tabs */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flex: "1",
            justifyContent: "center"
          }}>
            {[
              { id: "leads", title: "Lead Directory & Pipeline", desc: "Track conversions & check lead sources" },
              { id: "whatsapp", title: "WhatsApp Chat Sync", desc: "Direct templates & in-app chat logs" },
              { id: "products", title: "Product & Inventory Catalog", desc: "Leased units & occupancy charts" },
              { id: "quotations", title: "Quotations & PDF Generator", desc: "One-click proposal builder & downloads" },
              { id: "reports", title: "Reports & Funnel Analytics", desc: "Detailed performance reports & exports" }
            ].map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "20px",
                    background: active ? "rgba(59, 130, 246, 0.08)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${active ? "rgba(59, 130, 246, 0.25)" : "rgba(255,255,255,0.05)"}`,
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    textAlign: "left",
                    outline: "none"
                  }}
                >
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: active ? "rgba(59, 130, 246, 0.15)" : "rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: active ? "#55FF60" : "#fff",
                    transition: "all 0.3s ease",
                    flexShrink: 0
                  }}>
                    {tab.id === "leads" && <Target size={20} />}
                    {tab.id === "whatsapp" && <MessageSquare size={20} />}
                    {tab.id === "products" && <Users size={20} />}
                    {tab.id === "quotations" && <TrendingUp size={20} />}
                    {tab.id === "reports" && <BarChart3 size={20} />}
                  </div>
                  <div>
                    <h4 style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      color: active ? "#55FF60" : "#fff",
                      fontWeight: "600",
                      transition: "all 0.3s ease"
                    }}>
                      {tab.title}
                    </h4>
                    <span style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.5)"
                    }}>
                      {tab.desc}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Display Area */}
          <div style={{
            flex: "1.8",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: "20px",
            padding: isMobile ? "15px" : "25px",
            position: "relative",
            minHeight: "450px"
          }}>
            {/* Browser Header Mockup */}
            <div style={{
              width: "100%",
              marginBottom: "15px",
              paddingBottom: "10px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", gap: "6px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ff5f56" }}></div>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffbd2e" }}></div>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#27c93f" }}></div>
              </div>
              <span style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.3)",
                fontFamily: "monospace"
              }}>
                {activeTab === "leads" && "crm.gtelinq.com/leads"}
                {activeTab === "whatsapp" && "crm.gtelinq.com/whatsapp"}
                {activeTab === "products" && "crm.gtelinq.com/inventory"}
                {activeTab === "quotations" && "crm.gtelinq.com/quotes"}
                {activeTab === "reports" && "crm.gtelinq.com/reports"}
              </span>
              <div style={{ width: "24px" }}></div>
            </div>

            {/* Display Image inside browser frame */}
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
              <img
                src={
                  activeTab === "leads" ? crmLeadsImg :
                    activeTab === "whatsapp" ? crmWhatsappImg :
                      activeTab === "products" ? crmProductsImg :
                        activeTab === "quotations" ? crmQuotationsImg :
                          crmReportsImg
                }
                alt="CRM Panel Showcase"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  imageRendering: "-webkit-optimize-contrast"
                }}
              />
            </div>

            {/* Feature Description below image */}
            <div style={{ marginTop: "20px", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "15px" }}>
              {activeTab === "leads" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Lead Management Directory</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Monitor your sales funnel metrics from New, Contacted, Qualified, to Won and Lost. Capture lead data from sources like <strong>LinkedIn</strong>, <strong>Instagram</strong>, <strong>Website Forms</strong>, and <strong>WhatsApp</strong>. Distribute contacts automatically to agent pipelines and view activity logs in real-time.
                  </p>
                </div>
              )}
              {activeTab === "whatsapp" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>WhatsApp Chat Sync</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Communicate with prospects directly within the CRM panel. Send templates, read replies, and track active chats synced with <strong>Lead IDs</strong>. This centralized workspace keeps sales conversations audit-friendly and speeds up response times.
                  </p>
                </div>
              )}
              {activeTab === "products" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Product & Inventory Catalog</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Review allocations, check leased or reserved statuses, and manage monthly sales/rental subscriptions. Built-in charts visualize inventory types, regional distributions, and project allocations to forecast asset capacity instantly.
                  </p>
                </div>
              )}
              {activeTab === "quotations" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Quotations & Invoicing</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Create professional PDFs and email proposals directly to leads in one click. View dispatch history, verify approval metrics (Draft, Accepted, Rejected), and trace pipeline values (e.g., ₹14,13,052) automatically integrated with the ledger.
                  </p>
                </div>
              )}
              {activeTab === "reports" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Reports & Analysis</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Audit sales velocity, lead acquisition channel allocations, conversion rates, and lost opportunities. Generate graphical charts for funnel stages and export compiled datasheets directly in Excel formats for administrative review.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Can we connect Facebook Lead Forms directly?</h4>
            <p>Yes, leads are synced and assigned in real-time through Webhook connections to Facebook Lead Ads.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Does WhatsApp integration require Business API?</h4>
            <p>We support both custom WhatsApp Business API integrations and simplified barcode sync solutions.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default CRM;
